import { Button } from "../components/Button";
import { SectionHeader } from "../components/SectionHeader";
import { SmartLink } from "../components/SmartLink";
import { brand } from "../config/brand";
import { navigateTo } from "../lib/navigation";

type InfoPageKey = "our-story" | "press" | "privacy" | "terms" | "contact" | "not-found";

const pages: Record<
  InfoPageKey,
  {
    eyebrow: string;
    title: string;
    body: string[];
    action?: { label: string; href: string };
  }
> = {
  "our-story": {
    eyebrow: "Chef-led dining",
    title: "Our Story",
    body: [
      "OJ's Bistro brings Nigerian roots, global inspiration, and private-chef polish to tables built for real connection.",
      "The site is shaped around chef-led menus, dinner experiences, recipes, and social-first food storytelling."
    ],
    action: { label: "Book a chef", href: "/private-chef#book" }
  },
  press: {
    eyebrow: "Media and collaborations",
    title: "Press",
    body: [
      "For interviews, pop-ups, content partnerships, private dining features, and collaboration requests, contact the bistro directly.",
      "Include the date, publication or project, and the kind of food story you want to tell."
    ],
    action: { label: "Email the bistro", href: `mailto:${brand.email}` }
  },
  privacy: {
    eyebrow: "Privacy",
    title: "Privacy Policy",
    body: [
      "OJ's Bistro collects only the details needed to respond to inquiries, manage orders, handle bookings, and send newsletter updates when requested.",
      "Payment details are handled by Stripe Checkout when live payment keys are connected, so card numbers are not stored by this website."
    ],
    action: { label: "Contact us", href: `mailto:${brand.email}` }
  },
  terms: {
    eyebrow: "Terms",
    title: "Terms & Conditions",
    body: [
      "Menu orders, private-chef deposits, and event bookings are subject to availability and confirmation from OJ's Bistro.",
      "Deposits reserve planning time and may be applied toward the final experience. Final pricing depends on guest count, menu scope, timing, and location."
    ],
    action: { label: "Start booking", href: "/private-chef#book" }
  },
  contact: {
    eyebrow: "Contact",
    title: "Contact OJ's Bistro",
    body: [
      "For private dinners, event menus, collaborations, menu questions, and order support, send the bistro a direct note.",
      "Share the date, guest count, location, and the kind of experience you want so Chef OJ can respond with the right next step."
    ],
    action: { label: "Email the bistro", href: `mailto:${brand.email}` }
  },
  "not-found": {
    eyebrow: "Page not found",
    title: "That page is not on the menu.",
    body: [
      "The link may have moved, or it may not exist yet.",
      "Head back to the bistro homepage or use the navigation to find menus, private chef bookings, recipes, events, and shop items."
    ],
    action: { label: "Return home", href: "/" }
  }
};

export function InfoPage({ page }: { page: InfoPageKey }) {
  const content = pages[page];

  return (
    <main className="page-shell">
      <section className="info-page">
        <SectionHeader eyebrow={content.eyebrow} title={content.title} centered level={1} />
        <div className="info-copy">
          {content.body.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {content.action ? (
            content.action.href.startsWith("mailto:") ? (
              <SmartLink href={content.action.href} className="button button-dark">
                {content.action.label}
              </SmartLink>
            ) : (
              <Button onClick={() => navigateTo(content.action!.href)}>{content.action.label}</Button>
            )
          ) : null}
        </div>
      </section>
    </main>
  );
}
